import i18next from 'i18next';

import { scim } from '@talend/context';
import { AppGuidedTour } from '@talend/react-components';

import './GuidedTourHome.module.css';

const prepareTarget = () => {
	document.body.classList.add('snowflake-onboarding-highlight');
	const row = document.querySelector('.snowflake-item-highlight');
	let target;
	if (row) {
		target = row.querySelector('button[data-feature="connection.list.adddataset"]');
	} else {
		target = document.querySelector('button[id="datastore:add"]');
	}
	if (!target) {
		return false;
	}
	target.classList.add('snowflake-special-highlight');
	return !!row;
};

const getBasicSteps = () => {
	const meCache = scim.me.getCache();
	return [
		{
			selector: '[data-feature="connection.list"]',
			content: {
				header: i18next.t('tdc-app:GUIDE_TOUR_CONNECTIONS_STEP_HEADER', {
					defaultValue: 'Connections',
				}),
				body: () => (
					<p>
						{i18next.t('tdc-app:GUIDE_TOUR_CONNECTIONS_STEP_BODY', {
							defaultValue:
								'Here you can create and manage your Connections to systems that contain your Datasets, including SaaS applications, cloud storage, data lakes, databases, etc.',
						})}
					</p>
				),
			},
			disabled: !scim.me.hasEntitlement(meCache, 'CONNECTION_READ'),
		},
		{
			selector: '[data-feature="dataset.list"]',
			content: {
				header: i18next.t('tdc-app:GUIDE_TOUR_DATASETS_STEP_HEADER', {
					defaultValue: 'Datasets',
				}),
				body: () => (
					<>
						<p>
							{i18next.t('tdc-app:GUIDE_TOUR_DATASETS_STEP_BODY_1', {
								defaultValue: 'Here you can browse through and manage the datasets you added.',
							})}
						</p>
						<p>
							{i18next.t('tdc-app:GUIDE_TOUR_DATASETS_STEP_BODY_2', {
								defaultValue:
									'A dataset holds the raw data that can be used as raw material without affecting your original data.',
							})}
						</p>
					</>
				),
			},
			disabled: !scim.me.hasEntitlement(meCache, 'DATASET_READ'),
		},
		{
			selector: '[data-feature="semantic.list"]',
			content: {
				header: i18next.t('tdc-app:GUIDE_TOUR_SEMANTIC_STEP_HEADER', {
					defaultValue: 'Semantic types',
				}),
				body: () => (
					<>
						<p>
							{i18next.t('tdc-app:GUIDE_TOUR_SEMANTIC_STEP_BODY_1', {
								defaultValue: 'Here you can browse through and manage the semantic types.',
							})}
						</p>
						<p>
							{i18next.t('tdc-app:GUIDE_TOUR_SEMANTIC_STEP_BODY_2', {
								defaultValue:
									'A semantic type defines the kind of information the data represents, and performs discovery and validation of your data.',
							})}
						</p>
					</>
				),
			},
			disabled: !scim.me.hasOneOfEntitlements(meCache, [
				'TDQ_SEMANTIC_TYPE_MANAGE',
				'TDQ_SEMANTIC_TYPE_LIST',
			]),
		},
		{
			selector: '[data-feature="custom_attribute.list"]',
			content: {
				header: i18next.t('tdc-app:GUIDE_TOUR_ATTRIBUTES_STEP_HEADER', {
					defaultValue: 'Custom attributes',
				}),
				body: () => (
					<>
						<p>
							{i18next.t('tdc-app:GUIDE_TOUR_ATTRIBUTES_STEP_BODY_1', {
								defaultValue: 'Here you can browse through and manage the custom attributes.',
							})}
						</p>
						<p>
							{i18next.t('tdc-app:GUIDE_TOUR_ATTRIBUTES_STEP_BODY_2', {
								defaultValue:
									'A custom attribute allows you to create a flexible and generic tagging system to categorize and find your datasets.',
							})}
						</p>
					</>
				),
			},
			disabled: !scim.me.hasEntitlement(meCache, 'CUSTOM_ATTRIBUTE_READ'),
		},
	].filter(step => !step.disabled);
};

const getSnowflakeSteps = () => {
	const meCache = scim.me.getCache();
	return [
		{
			selector: '[data-feature="dataset.list"]',
			content: {
				header: i18next.t('tdc-app:GUIDE_TOUR_DATASETS_STEP_HEADER', {
					defaultValue: 'Datasets',
				}),
				body: () => (
					<>
						<p>
							{i18next.t('tdc-app:GUIDE_TOUR_DATASETS_STEP_BODY_1', {
								defaultValue: 'Here you can browse through and manage the datasets you added.',
							})}
						</p>
						<p>
							{i18next.t('tdc-app:GUIDE_TOUR_DATASETS_STEP_BODY_2', {
								defaultValue:
									'A dataset holds the raw data that can be used as raw material without affecting your original data.',
							})}
						</p>
					</>
				),
			},
			disabled: !scim.me.hasEntitlement(meCache, 'DATASET_READ'),
		},
		{
			selector: '[data-feature="custom_attribute.list"]',
			content: {
				header: i18next.t('tdc-app:GUIDE_TOUR_ATTRIBUTES_STEP_HEADER', {
					defaultValue: 'Custom attributes',
				}),
				body: () => (
					<>
						<p>
							{i18next.t('tdc-app:GUIDE_TOUR_ATTRIBUTES_STEP_BODY_1', {
								defaultValue: 'Here you can browse through and manage the custom attributes.',
							})}
						</p>
						<p>
							{i18next.t('tdc-app:GUIDE_TOUR_ATTRIBUTES_STEP_BODY_2', {
								defaultValue:
									'A custom attribute allows you to create a flexible and generic tagging system to categorize and find your datasets.',
							})}
						</p>
					</>
				),
			},
			disabled: !scim.me.hasEntitlement(meCache, 'CUSTOM_ATTRIBUTE_READ'),
		},
		{
			selector: '[data-feature="semantic.list"]',
			content: {
				header: i18next.t('tdc-app:GUIDE_TOUR_SEMANTIC_STEP_HEADER', {
					defaultValue: 'Semantic types',
				}),
				body: () => (
					<>
						<p>
							{i18next.t('tdc-app:GUIDE_TOUR_SEMANTIC_STEP_BODY_1', {
								defaultValue: 'Here you can browse through and manage the semantic types.',
							})}
						</p>
						<p>
							{i18next.t('tdc-app:GUIDE_TOUR_SEMANTIC_STEP_BODY_2', {
								defaultValue:
									'A semantic type defines the kind of information the data represents, and performs discovery and validation of your data.',
							})}
						</p>
					</>
				),
			},
			disabled: !scim.me.hasOneOfEntitlements(meCache, [
				'TDQ_SEMANTIC_TYPE_MANAGE',
				'TDQ_SEMANTIC_TYPE_LIST',
			]),
		},
		{
			selector: '[data-feature="connection.list"]',
			content: {
				header: i18next.t('tdc-app:GUIDE_TOUR_CONNECTIONS_STEP_HEADER', {
					defaultValue: 'Connections',
				}),
				body: () => (
					<p>
						{i18next.t('tdc-app:GUIDE_TOUR_CONNECTIONS_STEP_BODY', {
							defaultValue:
								'Create and manage your connections to systems that contain your datasets. This is where you’ll find your Snowflake connection, but also connections to SaaS applications, cloud storage, data lakes, databases, etc.',
						})}
					</p>
				),
			},
			disabled: !scim.me.hasEntitlement(meCache, 'CONNECTION_READ'),
		},
		{
			selector: '.snowflake-special-highlight',
			content: {
				header: i18next.t('tdc-app:GUIDE_TOUR_SNOWFLAKE_ADD_DATASET_STEP_HEADER', {
					defaultValue: 'Add dataset from connection',
				}),
				body: () => {
					const hasSnowflakeConnection = prepareTarget();
					return (
						<p>
							{hasSnowflakeConnection
								? i18next.t('tdc-app:GUIDE_TOUR_SNOWFLAKE_ADD_DATASET_STEP_BODY', {
										defaultValue:
											'From your newly created Snowflake connection, you can add a dataset in a glance. Ready to try?',
									})
								: i18next.t('tdc-app:GUIDE_TOUR_SNOWFLAKE_ADD_CONNECTION_STEP_BODY', {
										defaultValue:
											'Click on this icon to create a Snowflake connection. From there, you will be able to add a dataset. Ready to try?',
									})}
						</p>
					);
				},
			},
			disabled: !scim.me.hasEntitlement(meCache, 'CONNECTION_READ'),
		},
	].filter(step => !step.disabled);
};

const getSteps = (isSnowflake: boolean) => (isSnowflake ? getSnowflakeSteps : getBasicSteps)();

export function GuidedTourHome({ isSnowflake = false, ...rest }) {
	const steps = getSteps(isSnowflake);

	return (
		<AppGuidedTour
			appName="Data Inventory"
			steps={steps}
			onBeforeClose={() => document.body.classList.remove('snowflake-onboarding-highlight')}
			welcomeStepBody={
				isSnowflake && (
					<div>
						<p>
							{i18next.t('tdc-app:GUIDE_TOUR_SNOWFLAKE_CONNECTIONS_STEP_BODY', {
								defaultValue:
									'Watch this video and explore how Talend can help you better govern your data, make better business decisions, accelerate innovation and improve your operations.',
							})}
						</p>
						<iframe
							title={i18next.t('tdc-app:GUIDE_TOUR_SNOWFLAKE_INTRODUCTION_VIDEO', {
								defaultValue: 'Snowflake guided tour introduction video',
							})}
							width="360"
							height="200"
							src="https://www.youtube.com/embed/mC-DBYJUhfo"
							frameBorder="0"
							allowFullScreen
							allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
						/>
					</div>
				)
			}
			{...rest}
		/>
	);
}

GuidedTourHome.displayName = 'GuidedTourHome';
